/**
 * Phase 2a-4 — the "new artifact from template" picker. Pick an artifact type,
 * optionally name it, and Generate: tesoro renders a first-draft office file,
 * registers a born-ai_drafted studio asset, opens a CollaborativeDocument on
 * the seed, and returns the document id so the caller can open the editor.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useArtifactTypes, useGenerateFirstDraft } from '../../hooks/useArtifactStudio';
export function NewArtifactDialog({ projectId, open, onOpenChange, onGenerated }) {
    const { data: typesResp } = useArtifactTypes();
    const generate = useGenerateFirstDraft(projectId);
    const [artifactType, setArtifactType] = useState('');
    const [title, setTitle] = useState('');
    const types = typesResp?.types ?? [];
    const reset = ()=>{
        setArtifactType('');
        setTitle('');
    };
    const handleGenerate = async ()=>{
        if (!artifactType) return;
        const res = await generate.mutateAsync({
            artifactType,
            title
        });
        reset();
        onOpenChange(false);
        onGenerated?.(res.documentId, res.asset?.id ?? '');
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.artifactStudio.newDialog,
            children: [
                /*#__PURE__*/ _jsx(DialogHeader, {
                    children: /*#__PURE__*/ _jsx(DialogTitle, {
                        children: "New artifact"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4 py-2",
                    children: [
                        /*#__PURE__*/ _jsxs(Select, {
                            value: artifactType,
                            onValueChange: setArtifactType,
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    "data-testid": Testids.artifactStudio.typeSelect,
                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                        placeholder: "Choose an artifact type"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SelectContent, {
                                    children: types.map((t)=>/*#__PURE__*/ _jsxs(SelectItem, {
                                            value: t.key,
                                            children: [
                                                t.displayName,
                                                " · ",
                                                t.fileType.toUpperCase(),
                                                t.legalReviewRequired ? ' · ⚖ legal review' : ''
                                            ]
                                        }, t.key))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            placeholder: "Title (optional)",
                            value: title,
                            onChange: (e)=>setTitle(e.target.value),
                            "data-testid": Testids.artifactStudio.titleInput
                        }),
                        generate.isError && /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-destructive",
                            children: "Generation failed. Please try again."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(DialogFooter, {
                    children: /*#__PURE__*/ _jsx(Button, {
                        onClick: handleGenerate,
                        disabled: !artifactType || generate.isPending,
                        "data-testid": Testids.artifactStudio.generateSubmit,
                        children: generate.isPending ? 'Generating…' : 'Generate draft'
                    })
                })
            ]
        })
    });
}
